import React from 'react';
import { useTranslation } from 'react-i18next';

const LEVEL_OPTIONS_BASE = [
  { value: '', labelKey: 'admin.creators.allLevels', labelFallback: 'Tous les niveaux', color: 'rgba(255,255,255,0.7)' },
  { value: 'Junior', label: 'Junior', color: '#6b7280' },
  { value: 'Rising', label: 'Rising', color: '#3B82F6' },
  { value: 'Senior', label: 'Senior', color: '#FF6B00' },
  { value: 'Elite', label: 'Elite', color: '#FFD700' },
];

const CreatorsFilters = ({
  search, onSearch,
  serverFilter, onServerFilter,
  statusFilter, onStatusFilter,
  levelFilter, onLevelFilter,
  tierFilter, onTierFilter,
  managerFilter, onManagerFilter,
  managers,
  whatsappFilter, onWhatsappFilter,
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Search */}
      <div className="relative w-full sm:flex-1 sm:min-w-[200px]">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          style={{ color: 'rgba(255,255,255,0.3)' }}
        >
          <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.4" />
          <path d="M9.5 9.5L12.5 12.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
        <input
          type="text"
          placeholder={t('admin.creators.searchPlaceholder', 'Rechercher par username, TikTok ID ou WhatsApp...')}
          value={search}
          onChange={(e) => onSearch(e?.target?.value)}
          className="w-full pl-9 pr-4 py-2 rounded-lg text-base outline-none transition-colors"
          style={{
            backgroundColor: '#111111',
            border: '1px solid #1f1f1f',
            color: 'rgba(255,255,255,0.8)',
            fontFamily: 'Inter, sans-serif',
          }}
          onFocus={(e) => { e.target.style.borderColor = 'var(--color-primary)'; }}
          onBlur={(e) => { e.target.style.borderColor = '#1f1f1f'; }}
        />
      </div>
      {/* Server filter */}
      <select
        value={serverFilter}
        onChange={(e) => onServerFilter(e?.target?.value)}
        className="px-3 py-2 rounded-lg text-base outline-none cursor-pointer"
        style={{
          backgroundColor: '#111111',
          border: '1px solid #1f1f1f',
          color: 'rgba(255,255,255,0.7)',
          fontFamily: 'Inter, sans-serif',
        }}
      >
        <option value="">{t('admin.creators.allServers', 'Tous les serveurs')}</option>
        <option value="MENA">MENA</option>
        <option value="FR">FR</option>
        <option value="IT">IT</option>
        <option value="DE">DE</option>
      </select>
      {/* Level filter */}
      <select
        value={levelFilter || ''}
        onChange={(e) => onLevelFilter?.(e?.target?.value)}
        className="px-3 py-2 rounded-lg text-base outline-none cursor-pointer"
        style={{
          backgroundColor: '#111111',
          border: '1px solid #1f1f1f',
          color: levelFilter
            ? (LEVEL_OPTIONS_BASE?.find((o) => o?.value === levelFilter)?.color || 'rgba(255,255,255,0.7)')
            : 'rgba(255,255,255,0.7)',
          fontFamily: 'Inter, sans-serif',
        }}
      >
        {LEVEL_OPTIONS_BASE?.map((opt) => (
          <option key={opt?.value} value={opt?.value} style={{ color: opt?.color }}>
            {opt?.labelKey ? t(opt.labelKey, opt.labelFallback) : opt?.label}
          </option>
        ))}
      </select>
      {/* Tier filter */}
      <select
        value={tierFilter || ''}
        onChange={(e) => onTierFilter?.(e?.target?.value)}
        className="px-3 py-2 rounded-lg text-base outline-none cursor-pointer"
        style={{
          backgroundColor: '#111111',
          border: '1px solid #1f1f1f',
          color: 'rgba(255,255,255,0.7)',
          fontFamily: 'Inter, sans-serif',
        }}
      >
        <option value="">{t('admin.creators.allTiers', 'Tous les tiers')}</option>
        {Array.from({ length: 10 }, (_, i) => (
          <option key={i + 1} value={String(i + 1)}>T{i + 1}</option>
        ))}
      </select>
      {/* Status filter */}
      <select
        value={statusFilter}
        onChange={(e) => onStatusFilter(e?.target?.value)}
        className="px-3 py-2 rounded-lg text-base outline-none cursor-pointer"
        style={{
          backgroundColor: '#111111',
          border: '1px solid #1f1f1f',
          color: 'rgba(255,255,255,0.7)',
          fontFamily: 'Inter, sans-serif',
        }}
      >
        <option value="">{t('admin.creators.allStatuses', 'Tous les statuts')}</option>
        <option value="active_month">{t('admin.creators.statusActive', 'Actif ce mois')}</option>
        <option value="inactive_month">{t('admin.creators.statusInactive', 'Inactif ce mois')}</option>
      </select>
      {/* Manager filter — uses id (UUID) */}
      <select
        value={managerFilter || ''}
        onChange={(e) => onManagerFilter?.(e?.target?.value)}
        className="px-3 py-2 rounded-lg text-base outline-none cursor-pointer"
        style={{
          backgroundColor: '#111111',
          border: '1px solid #1f1f1f',
          color: 'rgba(255,255,255,0.7)',
          fontFamily: 'Inter, sans-serif',
        }}
      >
        <option value="">{t('admin.creators.allManagers', 'Tous les managers')}</option>
        {managers?.map((m) => (
          <option key={m?.id} value={m?.id}>{m?.name || m?.id}</option>
        ))}
      </select>
      {/* WhatsApp filter buttons */}
      <div className="flex items-center gap-1 rounded-lg p-1" style={{ backgroundColor: '#111111', border: '1px solid #1f1f1f' }}>
        {[
          { value: '', label: t('admin.creators.filterAll', 'Tous') },
          { value: 'with', label: t('admin.creators.filterWith', 'Avec ✓') },
          { value: 'sans', label: t('admin.creators.filterWithout', 'Sans ✗') },
        ]?.map(opt => (
          <button
            key={opt?.value}
            onClick={() => onWhatsappFilter?.(opt?.value)}
            className="px-2.5 py-1 rounded text-xs font-semibold transition-colors"
            style={{
              fontFamily: 'Inter, sans-serif',
              backgroundColor: whatsappFilter === opt?.value
                ? opt?.value === 'with' ? 'rgba(37,211,102,0.2)' : opt?.value === 'sans' ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.1)'
                : 'transparent',
              color: whatsappFilter === opt?.value
                ? opt?.value === 'with' ? '#25D366' : opt?.value === 'sans' ? '#ef4444' : 'rgba(255,255,255,0.8)'
                : 'rgba(255,255,255,0.4)',
              border: `1px solid ${whatsappFilter === opt?.value
                ? opt?.value === 'with' ? '#25D366' : opt?.value === 'sans' ? '#ef4444' : 'rgba(255,255,255,0.3)'
                : 'transparent'}`,
            }}
          >
            {opt?.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default CreatorsFilters;
